import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import styled, { keyframes } from 'styled-components';
import { ChevronLeft, ChevronRight, RotateCcw, Volume2, Shuffle, Edit2, Check, X, Plus, Trash2, LogIn, Settings } from 'lucide-react';
import { reviewSessionService } from '../../services/reviewSession.service';
import apiClient from '../../helpers/apiClient';
import { userActions } from '../../redux/actions';
import ReviewQuizGame from './ReviewQuizGame';
import ReviewMatchGame from './ReviewMatchGame';
import ReviewSortGame from './ReviewSortGame';
import ReviewPronounceGame from './ReviewPronounceGame';

// ─── Animations ─────────────────────────────────────────────────────────────

const fadeIn = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

// ─── Styled Components (white/light theme like SetDetail) ────────────────────

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #f8f9fc;
  padding: 28px 20px 60px;
  font-family: 'Segoe UI', sans-serif;
  animation: ${fadeIn} 0.35s ease;

  @media (max-width: 600px) {
    padding: 16px 8px 48px;
  }
`;

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 28px;
`;

const Title = styled.h1`
  font-size: 26px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 6px;
`;

const Subtitle = styled.p`
  font-size: 13px;
  color: #888;
  margin: 0;
`;

const TabBar = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-bottom: 24px;
`;

const Tab = styled.button<{ active: boolean }>`
  padding: 9px 22px;
  border-radius: 50px;
  border: 2px solid ${({ active }) => active ? '#667eea' : '#e0e0e0'};
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${({ active }) => active ? 'linear-gradient(135deg, #667eea, #764ba2)' : '#fff'};
  color: ${({ active }) => active ? '#fff' : '#555'};
  box-shadow: ${({ active }) => active ? '0 4px 12px rgba(102,126,234,0.3)' : '0 1px 3px rgba(0,0,0,0.06)'};
  &:hover { transform: translateY(-1px); }
`;

// ─── Flashcard ───────────────────────────────────────────────────────────────

const FlashcardScene = styled.div`
  perspective: 1200px;
  width: 100%;
  max-width: 680px;
  margin: 0 auto 20px;
  height: 320px;
  cursor: pointer;

  @media (max-width: 600px) {
    max-width: 100%;
    margin: 0 0 16px;
    height: 280px;
  }

  @media (max-width: 400px) {
    height: 250px;
  }
`;

const CardImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  border-radius: 16px;

  @media (max-width: 600px) {
    object-fit: contain;
    background: #f8f9fa;
    border-radius: 12px;
  }
`;

/* Compact text label overlaid on top of the image — auto-sized to text content */
const CardBackOverlay = styled.div`
  position: absolute;
  top: 18px;
  left: 50%;
  transform: translateX(-50%);
  display: inline-block;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-radius: 10px;
  padding: 8px 18px;
  max-width: 80%;
  box-shadow: 0 2px 12px rgba(0,0,0,0.15);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// ─── Settings Modal Styles ──────────────────────────────────────────────────

const SettingsOverlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.35);
  display: flex; align-items: center; justify-content: center; z-index: 1000;
`;

const SettingsCard = styled.div`
  background: #fff; border-radius: 18px; padding: 28px 32px;
  width: 100%; max-width: 400px; box-shadow: 0 16px 48px rgba(0,0,0,0.18);
`;

const SettingsTitle = styled.h2`
  font-size: 17px; font-weight: 700; color: #1a1a2e; margin: 0 0 20px;
  display: flex; align-items: center; gap: 8px;
`;

const SettingRow = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 0; border-bottom: 1px solid #f1f5f9;
  &:last-of-type { border-bottom: none; }
`;

const SettingLabel = styled.div`
  font-size: 14px; font-weight: 600; color: #334155;
  small { display: block; font-size: 11px; color: #94a3b8; font-weight: 400; margin-top: 2px; }
`;

const Toggle = styled.button<{ on: boolean }>`
  width: 50px; height: 28px; border-radius: 14px; border: none; cursor: pointer;
  background: ${({ on }) => on ? 'linear-gradient(90deg,#667eea,#764ba2)' : '#e2e8f0'};
  position: relative; transition: background 0.25s;
  flex-shrink: 0;
  &::after {
    content: ''; position: absolute; top: 3px;
    left: ${({ on }) => on ? '25px' : '3px'};
    width: 22px; height: 22px; border-radius: 50%;
    background: #fff; transition: left 0.25s;
    box-shadow: 0 1px 4px rgba(0,0,0,0.18);
  }
`;

const SettingsCloseBtn = styled.button`
  margin-top: 20px; width: 100%; padding: 10px;
  border: none; border-radius: 10px; cursor: pointer;
  background: linear-gradient(90deg,#667eea,#764ba2); color: #fff;
  font-weight: 600; font-size: 14px;
  &:hover { opacity: 0.9; }
`;

const FlashcardInner = styled.div<{ flipped: boolean }>`
  width: 100%;
  height: 100%;
  position: relative;
  transform-style: preserve-3d;
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  transform: ${({ flipped }) => flipped ? 'rotateY(180deg)' : 'rotateY(0deg)'};
`;

const CardFace = styled.div<{ back?: boolean }>`
  position: absolute; inset: 0;
  border-radius: 18px;
  backface-visibility: hidden;
  display: flex; flex-direction: column;
  justify-content: center; align-items: center;
  padding: 28px 36px; text-align: center;
  ${({ back }) => back
    ? 'background: #fff; border: 2px solid #e8ecf4; transform: rotateY(180deg); padding: 0; overflow: hidden;'
    : 'background: #fff; border: 2px solid #e8ecf4;'}
  box-shadow: 0 8px 30px rgba(0,0,0,0.1);
`;

const CardFront = styled.span<{ $fs?: number }>`
  font-size: ${p => p.$fs ?? 32}px; font-weight: 700; color: #1a1a2e; letter-spacing: -0.3px;
`;

const CardPhonetic = styled.span`
  font-size: 14px; color: #888; margin-top: 6px; font-style: italic;
`;

const CardBack = styled.span<{ $fs?: number }>`
  font-size: ${p => p.$fs ? Math.round(p.$fs * 0.75) : 24}px; font-weight: 700; color: #1a1a2e;
`;

const CardHint = styled.span`
  font-size: 11px; color: #bbb; margin-top: 10px;
`;

const NavRow = styled.div`
  display: flex; align-items: center; justify-content: center; gap: 14px; margin-bottom: 16px;
`;

const NavBtn = styled.button`
  width: 40px; height: 40px; border-radius: 50%;
  border: 2px solid #e0e0e0; background: #fff; color: #555;
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  transition: all 0.2s; box-shadow: 0 1px 4px rgba(0,0,0,0.08);
  &:hover { border-color: #667eea; color: #667eea; }
  &:disabled { opacity: 0.3; cursor: not-allowed; }
`;

const Counter = styled.span`
  font-size: 14px; color: #888; min-width: 60px; text-align: center; font-weight: 500;
`;

const CtrlRow = styled.div`
  display: flex; gap: 10px; justify-content: center; flex-wrap: wrap; margin-bottom: 28px;
`;

const CtrlBtn = styled.button`
  display: flex; align-items: center; gap: 6px; padding: 7px 16px;
  border-radius: 20px; border: 1.5px solid #e0e0e0;
  background: #fff; color: #555; font-size: 12px; font-weight: 500;
  cursor: pointer; transition: all 0.2s; box-shadow: 0 1px 3px rgba(0,0,0,0.06);
  &:hover { border-color: #667eea; color: #667eea; }
`;

const AudioCtrlBtn = styled.button`
  display: flex; align-items: center; gap: 7px; padding: 8px 20px;
  border-radius: 20px; border: none;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: #fff; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: all 0.2s;
  box-shadow: 0 4px 14px rgba(102,126,234,0.4);
  &:hover { transform: translateY(-1px); box-shadow: 0 6px 18px rgba(102,126,234,0.5); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

// ─── Login Banner ────────────────────────────────────────────────────────────

const LoginBanner = styled.div`
  background: linear-gradient(135deg, #eef2ff 0%, #f5f3ff 100%);
  border: 1.5px solid #c7d2fe;
  border-radius: 14px;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const BannerText = styled.span`
  font-size: 13px;
  color: #4338ca;
  font-weight: 500;
  flex: 1;
`;

const LoginBannerBtn = styled.button`
  display: flex; align-items: center; gap: 6px;
  padding: 8px 18px; border-radius: 20px; border: none;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: #fff; font-size: 13px; font-weight: 600;
  cursor: pointer; white-space: nowrap;
  box-shadow: 0 3px 10px rgba(102,126,234,0.35);
  transition: all 0.2s;
  &:hover { transform: translateY(-1px); box-shadow: 0 5px 15px rgba(102,126,234,0.45); }
`;

// ─── Login Modal ─────────────────────────────────────────────────────────────

const ModalOverlay = styled.div`
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(0,0,0,0.45);
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
`;

const LoginCard = styled.div`
  background: #fff; border-radius: 20px;
  padding: 28px 32px 24px;
  width: 100%; max-width: 400px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.25);
  animation: ${fadeIn} 0.3s ease;
  position: relative;
`;

const ModalCloseBtn = styled.button`
  position: absolute; top: 14px; right: 16px;
  background: none; border: none; cursor: pointer;
  color: #aaa; transition: color 0.2s;
  &:hover { color: #555; }
`;

const ModalTitle = styled.h2`
  font-size: 20px; font-weight: 800; color: #1e293b;
  text-align: center; margin: 0 0 4px;
`;

const ModalSubtitle = styled.p`
  font-size: 12px; color: #64748b;
  text-align: center; margin: 0 0 18px;
`;

const GoogleBtn = styled.button`
  width: 100%; padding: 10px 16px; border-radius: 10px;
  border: 2px solid #e2e8f0; background: #fff;
  display: flex; align-items: center; justify-content: center; gap: 10px;
  font-size: 14px; font-weight: 600; color: #1e293b;
  cursor: pointer; transition: all 0.2s;
  &:hover { border-color: #c7d2fe; box-shadow: 0 4px 12px rgba(0,0,0,0.1); transform: translateY(-1px); }
`;

const ModalDivider = styled.div`
  display: flex; align-items: center; gap: 10px;
  margin: 14px 0; color: #94a3b8; font-size: 12px;
  &::before, &::after { content: ''; flex: 1; height: 1px; background: #e2e8f0; }
`;

const ModalInput = styled.input<{ $error?: boolean }>`
  width: 100%; padding: 10px 12px; font-size: 14px;
  border: 2px solid ${p => p.$error ? '#ef4444' : '#e2e8f0'};
  border-radius: 10px; outline: none; box-sizing: border-box;
  transition: border-color 0.2s;
  &:focus { border-color: ${p => p.$error ? '#ef4444' : '#667eea'}; }
  &::placeholder { color: #94a3b8; }
`;

const ModalLabel = styled.label`
  font-size: 12px; font-weight: 600; color: #334155; display: block; margin-bottom: 4px;
`;

const ModalLoginBtn = styled.button`
  width: 100%; padding: 11px; border-radius: 10px; border: none;
  background: linear-gradient(90deg, #007bff, #00bfff);
  color: #fff; font-size: 14px; font-weight: 700;
  cursor: pointer; transition: all 0.2s; margin-top: 4px;
  &:hover { transform: translateY(-1px); box-shadow: 0 6px 18px rgba(0,123,255,0.35); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const ModalErrorText = styled.div`
  font-size: 12px; color: #ef4444; margin-top: 2px;
`;

// ─── Item list ───────────────────────────────────────────────────────────────

const SectionTitle = styled.h3`
  font-size: 14px; font-weight: 600; color: #555;
  margin: 20px 0 10px; padding-bottom: 6px;
  border-bottom: 2px solid #f0f0f0;
  display: flex; align-items: center; gap: 8px;
`;

const SentenceList = styled.div`
  display: flex; flex-direction: column; gap: 10px;
`;

const SentenceCard = styled.div`
  background: #fff;
  border: 1.5px solid #eaeef6;
  border-radius: 12px; padding: 14px 18px;
  display: flex; align-items: flex-start; justify-content: space-between; gap: 14px;
  transition: all 0.18s; box-shadow: 0 1px 4px rgba(0,0,0,0.05);
  &:hover { border-color: #c7d2fe; box-shadow: 0 3px 10px rgba(102,126,234,0.1); }
`;

const SentenceText = styled.div` flex: 1; `;

const SentenceEN = styled.div`
  font-size: 15px; font-weight: 600; color: #1a1a2e;
`;

const WordRow = styled.div`
  display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap; margin-bottom: 2px;
`;

const PhoneticText = styled.div`
  font-size: 12px; color: #7c3aed; font-style: italic; margin-bottom: 3px;
`;

const SentenceVI = styled.div`
  font-size: 13px; color: #6b7280;
`;

const PosBadge = styled.span`
  display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 10px; font-weight: 600;
  margin-bottom: 4px; background: #e0f2fe; color: #0284c7; margin-left: 6px;
`;

const SentenceActions = styled.div`
  display: flex; gap: 6px; flex-shrink: 0; align-items: center;
`;

const IconBtn = styled.button<{ danger?: boolean; success?: boolean }>`
  width: 30px; height: 30px; border-radius: 50%; border: 1.5px solid;
  border-color: ${({ danger, success }) => danger ? '#fecaca' : success ? '#bbf7d0' : '#e0e0e0'};
  background: ${({ danger, success }) => danger ? '#fff5f5' : success ? '#f0fdf4' : '#fff'};
  color: ${({ danger, success }) => danger ? '#ef4444' : success ? '#22c55e' : '#888'};
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  transition: all 0.18s;
  &:hover { opacity: 0.8; transform: scale(1.08); }
`;

const TypeBadge = styled.span<{ type: 'vocab' | 'sentence' }>`
  display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 10px; font-weight: 600;
  margin-bottom: 4px;
  ${({ type }) => type === 'vocab'
    ? 'background: #ede9fe; color: #7c3aed;'
    : 'background: #fef3c7; color: #d97706;'}
`;

const EditInput = styled.input`
  width: 100%; background: #f8f9fc; border: 1.5px solid #c7d2fe;
  border-radius: 6px; padding: 5px 10px; color: #1a1a2e; font-size: 14px;
  outline: none; margin-bottom: 4px;
  &:focus { border-color: #667eea; }
`;

const AddItemForm = styled.div`
  background: #f0f4ff; border: 1.5px dashed #a5b4fc;
  border-radius: 12px; padding: 14px 18px; display: flex; gap: 10px; align-items: flex-end;
  flex-wrap: wrap; margin-top: 8px;
`;

const AddInput = styled.input`
  flex: 1; min-width: 120px; background: #fff; border: 1.5px solid #e0e0e0;
  border-radius: 8px; padding: 7px 12px; color: #1a1a2e; font-size: 13px; outline: none;
  &::placeholder { color: #bbb; }
  &:focus { border-color: #667eea; }
`;

const AddSelect = styled.select`
  background: #fff; border: 1.5px solid #e0e0e0;
  border-radius: 8px; padding: 7px 12px; color: #1a1a2e; font-size: 13px; outline: none; cursor: pointer;
`;

const SaveBtn = styled.button`
  padding: 7px 18px; border-radius: 8px; border: none;
  background: linear-gradient(135deg, #667eea, #764ba2); color: #fff;
  font-weight: 600; cursor: pointer; font-size: 13px; transition: all 0.2s;
  &:hover { transform: translateY(-1px); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const AddBtn = styled.button`
  display: flex; align-items: center; gap: 6px; padding: 7px 16px;
  border-radius: 20px; border: 1.5px dashed #a5b4fc;
  background: #f0f4ff; color: #667eea; font-size: 12px; font-weight: 600;
  cursor: pointer; transition: all 0.2s; margin-top: 4px;
  &:hover { background: #e0e7ff; }
`;

const LoadingScreen = styled.div`
  min-height: 100vh; display: flex; align-items: center; justify-content: center;
  background: #f8f9fc; color: #888; font-size: 16px;
`;

const ErrorScreen = styled.div`
  min-height: 100vh; display: flex; flex-direction: column; align-items: center; justify-content: center;
  background: #f8f9fc; color: #888; gap: 10px;
`;

// ─── Main Component ──────────────────────────────────────────────────────────

const BackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255,255,255,0.9);
  border: 1.5px solid #e0e7ff;
  border-radius: 20px;
  padding: 8px 18px;
  font-size: 13px;
  font-weight: 600;
  color: #667eea;
  cursor: pointer;
  margin-bottom: 18px;
  box-shadow: 0 2px 8px rgba(102,126,234,0.12);
  transition: all 0.2s;
  &:hover {
    background: #667eea;
    color: #fff;
    border-color: #667eea;
    transform: translateX(-2px);
    box-shadow: 0 4px 14px rgba(102,126,234,0.3);
  }
`;

// ─── Student Name Modal ───────────────────────────────────────────────

const StudentOverlay = styled.div`
  position: fixed; inset: 0; z-index: 2000;
  background: rgba(15, 10, 40, 0.55);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  display: flex; align-items: center; justify-content: center;
  padding: 16px;
  overflow-y: auto;
`;

const StudentCard = styled.div`
  background: #fff; border-radius: 28px;
  padding: 28px 24px 24px;
  width: 100%; max-width: 440px;
  box-shadow: 0 30px 80px rgba(0,0,0,0.3);
  animation: ${fadeIn} 0.4s ease;
`;

const StudentSearchInput = styled.input`
  width: 100%; padding: 14px 16px 14px 44px; font-size: 16px;
  border: 2.5px solid #e2e8f0; border-radius: 16px;
  outline: none; box-sizing: border-box; transition: border-color 0.2s;
  &:focus { border-color: #667eea; box-shadow: 0 0 0 3px rgba(102,126,234,0.15); }
  &::placeholder { color: #94a3b8; }
`;

const StudentList = styled.div`
  max-height: 260px; overflow-y: auto; border-radius: 14px;
  border: 2px solid #f1f5f9; margin-top: 8px;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: #c7d2fe; border-radius: 4px; }
`;

const StudentItem = styled.button<{ $selected: boolean }>`
  width: 100%; padding: 12px 14px;
  display: flex; align-items: center; gap: 12px;
  border: none; background: ${p => p.$selected ? '#eef2ff' : '#fff'};
  border-left: 3px solid ${p => p.$selected ? '#667eea' : 'transparent'};
  cursor: pointer; text-align: left; transition: all 0.15s;
  &:hover { background: #f5f3ff; }
  &:not(:last-child) { border-bottom: 1px solid #f1f5f9; }
`;

const StudentAvatar = styled.div`
  width: 42px; height: 42px; border-radius: 50%; flex-shrink: 0;
  background: linear-gradient(135deg, #667eea, #764ba2);
  display: flex; align-items: center; justify-content: center;
  overflow: hidden; font-size: 20px; color: #fff; font-weight: 700;
  img { width: 100%; height: 100%; object-fit: cover; }
`;

const StartStudyBtn = styled.button<{ $ready: boolean }>`
  width: 100%; padding: 16px; border-radius: 18px; border: none;
  background: ${p => p.$ready
    ? 'linear-gradient(135deg, #667eea, #764ba2)'
    : '#e2e8f0'};
  color: ${p => p.$ready ? '#fff' : '#94a3b8'};
  font-size: 18px; font-weight: 800; cursor: ${p => p.$ready ? 'pointer' : 'not-allowed'};
  transition: all 0.25s; margin-top: 16px; letter-spacing: -0.3px;
  &:hover { ${p => p.$ready && 'transform: translateY(-2px); box-shadow: 0 10px 28px rgba(102,126,234,0.45);'} }
`;

const StudentBadge = styled.div`
  display: inline-flex; align-items: center; gap: 8px;
  background: linear-gradient(135deg, #eef2ff, #f5f3ff);
  border: 2px solid #c7d2fe; border-radius: 50px;
  padding: 8px 18px; font-size: 14px; font-weight: 600; color: #4338ca;
  margin-bottom: 16px; box-shadow: 0 2px 8px rgba(102,126,234,0.15);
  cursor: pointer;
  &:hover { background: #e0e7ff; }
`;

const ReportBtn = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 7px 16px; border-radius: 20px;
  border: 1.5px solid #c7d2fe; background: #eef2ff;
  color: #4338ca; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: all 0.2s; margin-top: 8px;
  &:hover { background: #667eea; color: #fff; border-color: #667eea; transform: translateY(-1px); }
`;

const ReviewDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const currentUser = useSelector((state: any) => state.authentication?.userInfo);
  const isAdminOrTeacher = currentUser?.role === 0 || currentUser?.role === 1;
  const isStudent = currentUser?.role === 2;
  const dispatch = useDispatch() as any;

  // ─── Auto-redirect out of Zalo / in-app WebView ───────────────────────────
  // Zalo WebView blocks SpeechRecognition. Redirect to Chrome/Safari immediately.
  useEffect(() => {
    const ua = navigator.userAgent || '';
    const isZalo = /ZaloApp|ZALO/i.test(ua);
    const isInAppBrowser =
      isZalo ||
      /Instagram|FBAN|FBAV|FB_IAB|MicroMessenger/i.test(ua) ||
      (ua.includes('Mobile') && /wv|WebView/.test(ua) && !ua.includes('CriOS'));

    if (!isInAppBrowser) return;

    const currentUrl = window.location.href;
    const isIOS = /iPad|iPhone|iPod/.test(ua);

    if (isIOS) {
      // iOS: open in Safari via x-safari scheme
      window.location.href = currentUrl.replace(/^https?:\/\//, 'x-safari-https://');
    } else {
      // Android: open in Chrome via intent scheme
      window.location.href =
        `intent:${currentUrl}#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(currentUrl)};end`;
    }
  }, []); // run once on mount


  // ─── Login Modal State ───────────────────────────────────────────────────
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginUserName, setLoginUserName] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginSubmitted, setLoginSubmitted] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  // ─── Student tracking state ──────────────────────────────────────────────────────
  const [studentName, setStudentName] = useState('');
  const [studentId, setStudentId] = useState<number | null>(null);
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [classStudents, setClassStudents] = useState<any[]>([]);
  const [studentSearch, setStudentSearch] = useState('');
  const [selectedStudentItem, setSelectedStudentItem] = useState<{ id: number | null; name: string } | null>(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [playLogs, setPlayLogs] = useState<any[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [classStudentsLoaded, setClassStudentsLoaded] = useState(false);
  const [studentAvatar, setStudentAvatar] = useState<string | null>(null);
  const [localPlayLogs, setLocalPlayLogs] = useState<{ game_type: 'quiz' | 'match' | 'sort' | 'pronounce'; score_pct: number }[]>([]);
  // Stores the IDs of pronunciation items already passed (persisted from previous sessions)
  const [pronunciationPassedIds, setPronunciationPassedIds] = useState<number[]>([]);

  const handleGoogleLogin = () => {
    const host = window.location.host;
    let backendUrl = 'http://localhost:5001';
    if (host.includes('tungkt.com')) backendUrl = 'https://tungkt.com';
    window.location.href = `${backendUrl}/api/auth/google`;
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginSubmitted(true);
    setLoginError('');
    if (!loginUserName || !loginPassword) return;
    setLoginLoading(true);
    try {
      await dispatch(userActions.login(loginUserName, loginPassword));
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      if (userInfo?.role !== undefined) {
        setShowLoginModal(false);
        if (userInfo.role === 0 || userInfo.role === 1) {
          navigate('/lesson-diary');
        } else if (userInfo.role === 2) {
          navigate('/student');
        } else {
          window.location.reload();
        }
      } else {
        setLoginError('Sai tên đăng nhập hoặc mật khẩu');
      }
    } catch {
      setLoginError('Sai tên đăng nhập hoặc mật khẩu');
    } finally {
      setLoginLoading(false);
    }
  };

  const [session, setSession] = useState<any>(null);
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [activeTab, setActiveTab] = useState<'quiz' | 'match' | 'sort' | 'pronounce' | null>(null);

  // Flashcard state
  const [cardIndex, setCardIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [shuffled, setShuffled] = useState(false);
  const [displayCards, setDisplayCards] = useState<any[]>([]);

  // Settings
  const [showSettings, setShowSettings] = useState(false);
  const [reverseMode, setReverseMode] = useState(false);
  const [autoPlayTTS, setAutoPlayTTS] = useState(true);
  const [includeSentences, setIncludeSentences] = useState(true); // mặc định bật
  const [cardFontSize, setCardFontSize] = useState(32); // mặc định nhỏ nhất (32px)

  // Font size options — hiển thị dạng chữ 'a' tăng dần
  const FONT_SIZES = [
    { value: 32, label: 'a', title: 'Nhỏ' },
    { value: 40, label: 'a', title: 'Vừa' },
    { value: 50, label: 'a', title: 'To' },
    { value: 62, label: 'a', title: 'Rất to' },
    { value: 76, label: 'a', title: 'Khổng lồ' },
  ];

  // TTS state
  const [loadingTTS, setLoadingTTS] = useState(false);

  // Edit state
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editFront, setEditFront] = useState('');
  const [editBack, setEditBack] = useState('');
  const [editPhonetic, setEditPhonetic] = useState('');

  // Add item state
  const [showAddForm, setShowAddForm] = useState(false);
  const [newType, setNewType] = useState<'vocab' | 'sentence'>('vocab');
  const [newFront, setNewFront] = useState('');
  const [newBack, setNewBack] = useState('');
  const [newPhonetic, setNewPhonetic] = useState('');
  const [saving, setSaving] = useState(false);

  // Custom image upload state
  const [uploadingImageId, setUploadingImageId] = useState<number | null>(null);
  const imgInputRef = React.useRef<HTMLInputElement>(null);
  // Remove-BG state: holds the chosen file + itemId while user decides what to do
  const [rembgPending, setRembgPending] = useState<{ file: File; itemId: number } | null>(null);
  const [removingBg, setRemovingBg] = useState(false);

  // Crop editor state
  const [cropState, setCropState] = useState<{ url: string; itemId: number } | null>(null);
  const cropCanvasRef = React.useRef<HTMLCanvasElement>(null);
  const [cropImgEl, setCropImgEl] = useState<HTMLImageElement | null>(null);
  const [cropScale, setCropScale] = useState(1);
  const [cropOffset, setCropOffset] = useState({ x: 0, y: 0 });
  const [cropDragging, setCropDragging] = useState(false);
  const [cropDragFrom, setCropDragFrom] = useState({ x: 0, y: 0, ox: 0, oy: 0 });
  const [cardDragOver, setCardDragOver] = useState(false);
  const CROP_W = 680, CROP_H = 320;

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [sessionRes, itemsRes] = await Promise.all([
          reviewSessionService.getById(Number(id)),
          reviewSessionService.getItems(Number(id)),
        ]);
        setSession(sessionRes.data);
        setItems(itemsRes.data || []);
      } catch {
        setError('Không tìm thấy bài ôn tập hoặc đã bị xoá.');
      } finally {
        setLoading(false);
      }
    };
    if (id) load();
  }, [id]);

  // Load class students for autocomplete
  useEffect(() => {
    if (!id) return;
    apiClient.get(`/api/review-sessions/${id}/class-students`)
      .then(res => {
        setClassStudents(res.data?.data || []);
        setClassStudentsLoaded(true);
      })
      .catch(() => {
        setClassStudentsLoaded(true); // coi như đã load xong dù lỗi
      });
  }, [id]);

  // Always prompt student name on every page load (no localStorage restore)
  useEffect(() => {
    if (!session || isAdminOrTeacher || isStudent) return;
    setShowStudentModal(true);
  }, [session, isAdminOrTeacher, isStudent]);

  // Auto-populate student info from logged-in account (role=2)
  // Ưu tiên lấy tên từ bảng students (qua studentId) để thống nhất với học sinh chọn tên từ dropdown
  // Chỉ chạy sau khi classStudents đã load xong (classStudentsLoaded=true) để tránh race condition
  useEffect(() => {
    if (!session || !isStudent || !currentUser) return;
    // Chờ đến khi API class-students đã trả về kết quả
    if (!classStudentsLoaded) return;

    // Tìm học sinh trong danh sách lớp bằng studentId (users.studentId = students.id)
    const matched = currentUser.studentId
      ? classStudents.find((cs: any) => cs.id === currentUser.studentId)
      : null;

    if (matched) {
      // Dùng tên từ bảng students — nhất quán với học sinh chọn qua dropdown
      setStudentName(matched.name);
      setStudentId(matched.id);                              // = students.id
      setStudentAvatar(matched.avatar || currentUser.avatar || null);
      // Load lịch sử luyện phát âm (async IIFE để tránh lỗi await trong useEffect)
      (async (sid: number, name: string) => {
        try {
          const params = new URLSearchParams();
          params.set('student_id', String(sid));
          params.set('student_name', name);
          const res = await apiClient.get(`/api/review-sessions/${id}/my-play-logs?${params}`);
          const myLogs: any[] = res.data?.data || [];
          // Deduplicate: keep best score per game_type
          const bestPerType: Record<string, number> = {};
          myLogs.forEach((l: any) => {
            const gt = l.game_type as string;
            if (bestPerType[gt] === undefined || l.score_pct > bestPerType[gt]) {
              bestPerType[gt] = l.score_pct;
            }
          });
          setLocalPlayLogs(
            Object.entries(bestPerType).map(([game_type, score_pct]) => ({
              game_type: game_type as 'quiz' | 'match' | 'sort' | 'pronounce',
              score_pct,
            }))
          );
          const pronounceLog = myLogs.find((l: any) => l.game_type === 'pronounce');
          if (pronounceLog?.passed_item_ids) {
            try { setPronunciationPassedIds(JSON.parse(pronounceLog.passed_item_ids)); } catch { setPronunciationPassedIds([]); }
          }
        } catch { /* silent */ }
      })(matched.id, matched.name);
    } else {
      // Fallback thực sự: classStudents đã load xong nhưng studentId không khớp hoặc null
      // Vẫn ưu tiên studentId để backend có thể resolve tên từ DB
      const name = currentUser.userName || currentUser.email || `Học sinh #${currentUser.id}`;
      setStudentName(name);
      setStudentId(currentUser.studentId ?? currentUser.id ?? null);
      if (currentUser.avatar) setStudentAvatar(currentUser.avatar);
    }
  }, [session, isStudent, currentUser, classStudents, classStudentsLoaded]); // classStudentsLoaded bắt buộc trong deps

  // Save a game result to the server + track locally for badge display
  const handleGameComplete = useCallback(async (gameType: 'quiz' | 'match' | 'sort' | 'pronounce', pct: number) => {
    const roundedPct = Math.round(pct);
    setLocalPlayLogs(prev => {
      const existing = prev.find(l => l.game_type === gameType);
      if (existing) {
        return prev.map(l => l.game_type === gameType ? { ...l, score_pct: roundedPct } : l);
      }
      return [...prev, { game_type: gameType, score_pct: roundedPct }];
    });
    if (!studentName) return;
    try {
      await apiClient.post(`/api/review-sessions/${id}/play-log`, {
        student_name: studentName,
        student_id: studentId,
        game_type: gameType,
        score_pct: roundedPct,
      });
    } catch { /* silent — don't break student's flow */ }
  }, [studentName, studentId, id]);

  // Called immediately each time a pronunciation item is passed (score >= 70)
  // Saves to server right away so progress is NOT lost if student exits
  const handlePronounceItemPassed = useCallback(async (itemId: number, allPassedIds: number[]) => {
    // Update local state first
    setPronunciationPassedIds(allPassedIds);

    const allItems = items.filter(i => !i.is_deleted);
    const pct = allItems.length > 0
      ? Math.round((allPassedIds.length / allItems.length) * 100)
      : 0;

    // Update local log badge
    setLocalPlayLogs(prev => {
      const existing = prev.find(l => l.game_type === 'pronounce');
      if (existing) {
        return prev.map(l => l.game_type === 'pronounce' ? { ...l, score_pct: pct } : l);
      }
      return [...prev, { game_type: 'pronounce', score_pct: pct }];
    });

    if (!studentName) return;
    try {
      await apiClient.post(`/api/review-sessions/${id}/play-log`, {
        student_name: studentName,
        student_id: studentId,
        game_type: 'pronounce',
        score_pct: pct,
        passed_item_ids: JSON.stringify(allPassedIds),
      });
    } catch { /* silent — don't break student's flow */ }
  }, [studentName, studentId, id, items]);

  // Load and show report (teacher/admin only)
  const handleViewReport = async () => {
    setLoadingLogs(true);
    try {
      const res = await apiClient.get(`/api/review-sessions/${id}/play-logs`);
      setPlayLogs(res.data?.data || []);
      setShowReportModal(true);
    } catch { alert('Không thể tải báo cáo'); }
    finally { setLoadingLogs(false); }
  };

  // Confirm student — requires selecting from dropdown (no free-text)
  const handleConfirmStudent = async () => {
    if (!selectedStudentItem) return;
    const s = classStudents.find((cs: any) => cs.id === selectedStudentItem.id);
    const name = selectedStudentItem.name;
    const sid = selectedStudentItem.id;
    setStudentName(name);
    setStudentId(sid);
    setStudentAvatar(s?.avatar || null);
    // DO NOT clear localPlayLogs here — keep showing old data until server responds
    setShowStudentModal(false);
    setSelectedStudentItem(null);
    setStudentSearch('');

    // Tải kết quả lần trước của học sinh này trong buổi ôn này
    try {
      const params = new URLSearchParams();
      if (sid != null) params.set('student_id', String(sid));
      params.set('student_name', name);
      const res = await apiClient.get(`/api/review-sessions/${id}/my-play-logs?${params}`);
      const myLogs: any[] = res.data?.data || [];
      // Clear and rebuild — even if empty (student may have switched to another student with no logs)
      // Deduplicate: keep best score per game_type
      const bestPerType: Record<string, number> = {};
      myLogs.forEach((l: any) => {
        const gt = l.game_type as string;
        if (bestPerType[gt] === undefined || l.score_pct > bestPerType[gt]) {
          bestPerType[gt] = l.score_pct;
        }
      });
      setLocalPlayLogs(
        Object.entries(bestPerType).map(([game_type, score_pct]) => ({
          game_type: game_type as 'quiz' | 'match' | 'sort' | 'pronounce',
          score_pct,
        }))
      );
      // Restore pronunciationPassedIds from existing pronounce log
      const pronounceLog = myLogs.find((l: any) => l.game_type === 'pronounce');
      if (pronounceLog?.passed_item_ids) {
        try {
          const ids: number[] = JSON.parse(pronounceLog.passed_item_ids);
          setPronunciationPassedIds(ids);
        } catch {
          setPronunciationPassedIds([]);
        }
      } else {
        setPronunciationPassedIds([]);
      }
    } catch {
      // silent — kết quả lịch sử không ảnh hưởng trải nghiệm làm bài
      // If API fails, keep whatever was in localPlayLogs already (don't clear)
    }
  };

  // Resolve avatar URL
  const resolveAvatarUrl = (url: string | null) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    const base = process.env.REACT_APP_API_URL || '';
    return `${base.replace(/\/$/, '')}${url}`;
  };

  // Group play logs by student for the report table (one row per student)
  const groupedReport = useMemo(() => {
    const map: Record<string, {
      student_name: string;
      student_id: number | null;
      logs: Record<'quiz' | 'match' | 'sort' | 'pronounce', { score_pct: number; played_at: string }[]>;
    }> = {};
    playLogs.forEach((log: any) => {
      if (!map[log.student_name]) {
        map[log.student_name] = {
          student_name: log.student_name,
          student_id: log.student_id ?? null,
          logs: { quiz: [], match: [], sort: [], pronounce: [] },
        };
      }
      if (['quiz', 'match', 'sort', 'pronounce'].includes(log.game_type)) {
        map[log.student_name].logs[log.game_type as 'quiz' | 'match' | 'sort' | 'pronounce'].push({
          score_pct: log.score_pct,
          played_at: log.played_at,
        });
      }
    });
    return Object.values(map).sort((a, b) => a.student_name.localeCompare(b.student_name, 'vi'));
  }, [playLogs]);

  const getStudentAvatarById = (sid: number | null) => {
    if (!sid) return null;
    const s = classStudents.find((cs: any) => cs.id === sid);
    return s?.avatar || null;
  };

  // Stable filtered lists — must be hooks (useMemo) BEFORE any early returns
  const vocabItems = useMemo(() => items.filter(i => i.type === 'vocab'), [items]);
  const sentenceItems = useMemo(() => items.filter(i => i.type === 'sentence'), [items]);

  // ─── Custom image upload ─────────────────────────────────────────────────
  const getImageBase = () => {
    const base = process.env.REACT_APP_API_URL || '';
    return base.replace(/\/$/, '');
  };

  const resolveImageUrl = (url: string | null | undefined) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${getImageBase()}${url}`;
  };

  const handleImageUpload = async (file: File, itemId: number) => {
    if (!file.type.startsWith('image/')) { alert('Chỉ chấp nhận file ảnh!'); return; }
    setUploadingImageId(itemId);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await apiClient.post(`/api/review-sessions/items/${itemId}/custom-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.success) {
        const newUrl = res.data.data.custom_image_url;
        // Update local state immediately
        setItems(prev => prev.map(it => it.id === itemId ? { ...it, custom_image_url: newUrl } : it));
        setDisplayCards(prev => prev.map(it => it.id === itemId ? { ...it, custom_image_url: newUrl } : it));
      }
    } catch (err: any) {
      alert('Upload ảnh thất bại: ' + (err?.response?.data?.message || err.message));
    } finally {
      setUploadingImageId(null);
    }
  };

  const handleImageDelete = async (itemId: number) => {
    if (!window.confirm('Xóa ảnh tùy chỉnh này?')) return;
    setUploadingImageId(itemId);
    try {
      await apiClient.delete(`/api/review-sessions/items/${itemId}/custom-image`);
      setItems(prev => prev.map(it => it.id === itemId ? { ...it, custom_image_url: null } : it));
      setDisplayCards(prev => prev.map(it => it.id === itemId ? { ...it, custom_image_url: null } : it));
    } catch (err: any) {
      alert('Xóa ảnh thất bại: ' + (err?.response?.data?.message || err.message));
    } finally {
      setUploadingImageId(null);
    }
  };

  // ─── Remove Background (client-side via @imgly/background-removal) ──────────
  const handleRemoveBgAndCrop = async () => {
    if (!rembgPending) return;
    const { file, itemId } = rembgPending;
    setRembgPending(null);
    setRemovingBg(true);
    try {
      // Dynamic import so the heavy WASM model only loads when needed
      const { removeBackground } = await import('@imgly/background-removal');
      const resultBlob = await removeBackground(file);
      const processedFile = new File([resultBlob], 'rembg-result.png', { type: 'image/png' });
      openCropEditor(processedFile, itemId);
    } catch (err: any) {
      alert('Xóa nền thất bại: ' + (err?.message || err));
    } finally {
      setRemovingBg(false);
    }
  };

  // ─── Crop editor helpers ───────────────────────────────────────────────────
  const openCropEditor = (file: File, itemId: number) => {
    if (!file.type.startsWith('image/')) { alert('Chỉ chấp nhận file ảnh!'); return; }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      setCropImgEl(img);
      const initScale = Math.max(CROP_W / img.width, CROP_H / img.height);
      setCropScale(Math.max(0.3, Math.min(initScale, 4)));
      setCropOffset({
        x: (CROP_W - img.width * initScale) / 2,
        y: (CROP_H - img.height * initScale) / 2,
      });
    };
    img.src = url;
    setCropState({ url, itemId });
  };

  // Redraw canvas whenever crop params change — always paint white background first
  React.useEffect(() => {
    if (!cropState || !cropImgEl || !cropCanvasRef.current) return;
    const ctx = cropCanvasRef.current.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, CROP_W, CROP_H);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, CROP_W, CROP_H);
    ctx.drawImage(cropImgEl, cropOffset.x, cropOffset.y, cropImgEl.width * cropScale, cropImgEl.height * cropScale);
  }, [cropImgEl, cropScale, cropOffset, cropState]);

  const handleCropMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCropDragging(true);
    setCropDragFrom({ x: e.clientX, y: e.clientY, ox: cropOffset.x, oy: cropOffset.y });
  };
  const handleCropMouseMove = (e: React.MouseEvent) => {
    if (!cropDragging) return;
    e.preventDefault();
    setCropOffset({
      x: cropDragFrom.ox + (e.clientX - cropDragFrom.x),
      y: cropDragFrom.oy + (e.clientY - cropDragFrom.y),
    });
  };
  const handleCropMouseUp = (e: React.MouseEvent) => {
    e.preventDefault();
    setCropDragging(false);
  };
  const handleCropWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const factor = e.deltaY < 0 ? 1.08 : 0.93;
    const ns = Math.max(0.2, Math.min(5, cropScale * factor));
    const r = ns / cropScale;
    setCropScale(ns);
    setCropOffset(prev => ({
      x: CROP_W / 2 - (CROP_W / 2 - prev.x) * r,
      y: CROP_H / 2 - (CROP_H / 2 - prev.y) * r,
    }));
  };

  const handleCropConfirm = () => {
    if (!cropCanvasRef.current || !cropState) return;
    const { itemId, url } = cropState;
    setUploadingImageId(itemId);
    setCropState(null);
    setCropImgEl(null);
    // Composite onto white background before export so saved file is always white, not black
    const srcCanvas = cropCanvasRef.current;
    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = CROP_W;
    exportCanvas.height = CROP_H;
    const ctx = exportCanvas.getContext('2d')!;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, CROP_W, CROP_H);
    ctx.drawImage(srcCanvas, 0, 0);
    exportCanvas.toBlob(async (blob) => {
      URL.revokeObjectURL(url);
      if (!blob) { setUploadingImageId(null); return; }
      const f = new File([blob], 'custom-image.jpg', { type: 'image/jpeg' });
      await handleImageUpload(f, itemId);
    }, 'image/jpeg', 0.92);
  };

  const handleCropCancel = () => {
    if (cropState) URL.revokeObjectURL(cropState.url);
    setCropState(null);
    setCropImgEl(null);
  };

  // Rebuild displayCards when display MODE changes (shuffle/sentences toggle)
  // → reset back to card 0 because the list order may be completely different
  useEffect(() => {
    const pool = includeSentences
      ? items.filter(i => !i.is_deleted)
      : items.filter(i => i.type === 'vocab');
    if (shuffled) {
      const arr = [...pool];
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
      setDisplayCards(arr);
    } else {
      setDisplayCards(pool);
    }
    setCardIndex(0);
    setFlipped(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shuffled, includeSentences]);

  // Rebuild displayCards when items data changes (image added, content edited, item added/deleted)
  // → KEEP current position so teacher can immediately see the result after editing
  useEffect(() => {
    const pool = includeSentences
      ? items.filter(i => !i.is_deleted)
      : items.filter(i => i.type === 'vocab');
    let newCards: any[];
    if (shuffled) {
      // Re-shuffle only changes the subset but we want to keep the same card visible.
      // Find where the current card ended up in the new pool and keep it.
      newCards = displayCards
        .map(dc => pool.find(p => p.id === dc.id)) // update each card's data
        .filter(Boolean) as any[];
      // Append any newly added items (not yet in displayCards)
      const existingIds = new Set(newCards.map((c: any) => c.id));
      pool.forEach(p => { if (!existingIds.has(p.id)) newCards.push(p); });
    } else {
      newCards = pool;
    }
    setDisplayCards(newCards);
    // Clamp cardIndex in case items were deleted and the index is now out of bounds
    setCardIndex(prev => Math.min(prev, Math.max(0, newCards.length - 1)));
    // Don't touch flipped — let teacher see the back of the card after adding image
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // ─── TTS (same pattern as SetDetailView) ────────────────────────────────

  const getFullAudioUrl = (audioUrl: string) => {
    if (!audioUrl) return '';
    if (audioUrl.startsWith('http://') || audioUrl.startsWith('https://')) return audioUrl;
    const base = process.env.REACT_APP_API_URL || '';
    return `${base}${audioUrl.startsWith('/') ? '' : '/'}${audioUrl}`;
  };

  const playTTSAudio = async (word: string) => {
    if (!word || loadingTTS) return;
    setLoadingTTS(true);
    try {
      const response = await apiClient.get(`/api/flashcards/tts/${encodeURIComponent(word)}`);
      if (response.data?.success && response.data?.audio_url) {
        const audio = new Audio(getFullAudioUrl(response.data.audio_url));
        await audio.play();
        return;
      }
    } catch {
      // fall through to Web Speech API
    } finally {
      setLoadingTTS(false);
    }

    // Fallback: browser native TTS
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(word);
      utterance.lang = 'en-US';
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utterance);
    }
  };

  // ─── Flashcard nav ───────────────────────────────────────────────────────

  const handleFlip = () => setFlipped(f => !f);
  const handlePrev = () => { setFlipped(false); setCardIndex(i => Math.max(0, i - 1)); };
  const handleNext = () => { setFlipped(false); setCardIndex(i => Math.min(displayCards.length - 1, i + 1)); };
  const handleReset = () => { setCardIndex(0); setFlipped(false); };

  // Auto-play TTS when card changes (if autoPlayTTS is on)
  const prevCardIndexRef = useRef<number>(-1);
  useEffect(() => {
    if (!autoPlayTTS) return;
    if (prevCardIndexRef.current === cardIndex) return;
    prevCardIndexRef.current = cardIndex;
    const card = displayCards[cardIndex];
    if (!card) return;
    // Play the word that's shown on the front face
    const textToPlay = reverseMode ? card.back : card.front;
    playTTSAudio(textToPlay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardIndex, autoPlayTTS]);

  // ─── Edit ────────────────────────────────────────────────────────────────

  const startEdit = (item: any) => {
    setEditingId(item.id);
    setEditFront(item.front);
    setEditBack(item.back);
    setEditPhonetic(item.phonetic || '');
  };

  const cancelEdit = () => setEditingId(null);

  const saveEdit = async (item: any) => {
    setSaving(true);
    try {
      const res = await reviewSessionService.updateItem(item.id, {
        front: editFront,
        back: editBack,
        phonetic: editPhonetic,
        type: item.type,
      });
      setItems(prev => prev.map(i => i.id === item.id ? res.data : i));
      setEditingId(null);
    } catch { alert('Lỗi khi lưu!'); }
    finally { setSaving(false); }
  };

  const handleDeleteItem = async (itemId: number) => {
    if (!window.confirm('Xoá mục này?')) return;
    await reviewSessionService.deleteItem(itemId);
    setItems(prev => prev.filter(i => i.id !== itemId));
  };

  const handleAddItem = async () => {
    if (!newFront.trim() || !newBack.trim()) { alert('Vui lòng điền đầy đủ!'); return; }
    setSaving(true);
    try {
      const res = await reviewSessionService.addItem(Number(id), {
        type: newType,
        front: newFront,
        back: newBack,
        phonetic: newPhonetic,
      });
      setItems(prev => [...prev, res.data]);
      setNewFront(''); setNewBack(''); setNewPhonetic(''); setShowAddForm(false);
    } catch { alert('Lỗi khi thêm!'); }
    finally { setSaving(false); }
  };

  // ─── Render helpers ──────────────────────────────────────────────────────

  const renderItemRow = (item: any) => {
    const isEditing = editingId === item.id;
    return (
      <SentenceCard key={item.id}>
        <SentenceText>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <TypeBadge type={item.type}>{item.type === 'vocab' ? '📚 Từ vựng' : '💬 Mẫu câu'}</TypeBadge>
            {item.type === 'vocab' && item.part_of_speech && <PosBadge>{item.part_of_speech}</PosBadge>}
          </div>
          {isEditing ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <EditInput
                value={editFront}
                onChange={e => setEditFront(e.target.value)}
                placeholder="Tiếng Anh"
              />
              <EditInput
                value={editPhonetic}
                onChange={e => setEditPhonetic(e.target.value)}
                placeholder="Phiên âm (phonics) — ví dụ: /ˈæpəl/"
                style={{ fontSize: 12, color: '#7c3aed' }}
              />
              <EditInput
                value={editBack}
                onChange={e => setEditBack(e.target.value)}
                placeholder="Tiếng Việt"
              />
            </div>
          ) : (
            <>
              <WordRow>
                <SentenceEN>{item.front}</SentenceEN>
                <SentenceVI>— {item.back}</SentenceVI>
              </WordRow>
              {item.phonetic && <PhoneticText>/{item.phonetic.replace(/^\/|\/$/g, '')}/</PhoneticText>}
            </>
          )}
        </SentenceText>
        <SentenceActions>
          <IconBtn
            onClick={() => playTTSAudio(item.front)}
            title="Nghe phát âm"
            disabled={loadingTTS}
          >
            <Volume2 size={13} />
          </IconBtn>
          {isAdminOrTeacher && (
            isEditing ? (
              <>
                <IconBtn success onClick={() => saveEdit(item)} title="Lưu" disabled={saving}>
                  <Check size={13} />
                </IconBtn>
                <IconBtn danger onClick={cancelEdit} title="Huỷ">
                  <X size={13} />
                </IconBtn>
              </>
            ) : (
              <>
                <IconBtn onClick={() => startEdit(item)} title="Sửa">
                  <Edit2 size={13} />
                </IconBtn>
                <IconBtn danger onClick={() => handleDeleteItem(item.id)} title="Xoá">
                  <Trash2 size={13} />
                </IconBtn>
              </>
            )
          )}
        </SentenceActions>
      </SentenceCard>
    );
  };

  if (loading) return <LoadingScreen>⏳ Đang tải bài ôn tập...</LoadingScreen>;
  if (error || !session) return <ErrorScreen><div>❌ {error}</div></ErrorScreen>;

  const currentCard = displayCards[cardIndex];

  return (
    <PageWrapper>
      {/* Login Modal */}
      {showLoginModal && (
        <ModalOverlay onClick={(e) => { if (e.target === e.currentTarget) setShowLoginModal(false); }}>
          <LoginCard>
            <ModalCloseBtn onClick={() => setShowLoginModal(false)}><X size={18} /></ModalCloseBtn>
            <div style={{ textAlign: 'center', marginBottom: 4, marginTop: -4 }}>
              <img src="/kt-english-logo.png" alt="KT English Logo" style={{ width: 160, height: 'auto', filter: 'drop-shadow(0 6px 16px rgba(37,99,235,0.25))' }} />
            </div>
            {/* Google login — on top */}
            <GoogleBtn type="button" onClick={handleGoogleLogin}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M19.6 10.227c0-.709-.064-1.39-.182-2.045H10v3.868h5.382a4.6 4.6 0 01-1.996 3.018v2.51h3.232c1.891-1.742 2.982-4.305 2.982-7.35z" fill="#4285F4" />
                <path d="M10 20c2.7 0 4.964-.895 6.618-2.423l-3.232-2.509c-.895.6-2.04.955-3.386.955-2.605 0-4.81-1.76-5.595-4.123H1.064v2.59A9.996 9.996 0 0010 20z" fill="#34A853" />
                <path d="M4.405 11.9c-.2-.6-.314-1.24-.314-1.9 0-.66.114-1.3.314-1.9V5.51H1.064A9.996 9.996 0 000 10c0 1.614.386 3.14 1.064 4.49l3.34-2.59z" fill="#FBBC05" />
                <path d="M10 3.977c1.468 0 2.786.505 3.823 1.496l2.868-2.868C14.959.99 12.695 0 10 0 6.09 0 2.71 2.24 1.064 5.51l3.34 2.59C5.19 5.736 7.395 3.977 10 3.977z" fill="#EA4335" />
              </svg>
              Đăng nhập với Google
            </GoogleBtn>

            <ModalDivider>hoặc</ModalDivider>

            <form onSubmit={handleLoginSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <ModalLabel>Tên đăng nhập</ModalLabel>
                <ModalInput
                  type="text"
                  value={loginUserName}
                  onChange={e => setLoginUserName(e.target.value)}
                  placeholder="Nhập tên đăng nhập"
                  $error={loginSubmitted && !loginUserName}
                />
                {loginSubmitted && !loginUserName && <ModalErrorText>Vui lòng nhập tên đăng nhập</ModalErrorText>}
              </div>
              <div>
                <ModalLabel>Mật khẩu</ModalLabel>
                <div style={{ position: 'relative' }}>
                  <ModalInput
                    type={showLoginPassword ? 'text' : 'password'}
                    value={loginPassword}
                    onChange={e => setLoginPassword(e.target.value)}
                    placeholder="Nhập mật khẩu"
                    $error={loginSubmitted && !loginPassword}
                    style={{ paddingRight: 40, width: '100%', boxSizing: 'border-box' }}
                  />
                  <span
                    style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', fontSize: 18, color: '#64748b' }}
                    onClick={() => setShowLoginPassword(p => !p)}
                  >{showLoginPassword ? '👁' : '👁️'}</span>
                </div>
                {loginSubmitted && !loginPassword && <ModalErrorText>Vui lòng nhập mật khẩu</ModalErrorText>}
              </div>
              {loginError && <ModalErrorText>{loginError}</ModalErrorText>}
              <ModalLoginBtn type="submit" disabled={loginLoading}>
                {loginLoading ? '⏳ Đang đăng nhập...' : 'Đăng nhập'}
              </ModalLoginBtn>
            </form>
          </LoginCard>
        </ModalOverlay>
      )}

      <Container>
        {/* Back button — shown to logged-in students */}
        {isStudent && (
          <BackBtn onClick={() => navigate('/student/reviews')}>
            <ChevronLeft size={15} /> Quay lại danh sách bài ôn
          </BackBtn>
        )}

        {/* Login Prompt Banner — shown to guest users */}
        {!currentUser && (
          <LoginBanner>
            <BannerText>
              🎓 Đăng nhập để ôn luyện thêm bài tập!
            </BannerText>
            <LoginBannerBtn onClick={() => setShowLoginModal(true)}>
              <LogIn size={15} /> Đăng Nhập
            </LoginBannerBtn>
          </LoginBanner>
        )}

        {/* Header */}
        <Header>
          <Title>📖 {session.name}</Title>
          <Subtitle>
            {vocabItems.length} từ vựng &nbsp;·&nbsp; {sentenceItems.length} mẫu câu
          </Subtitle>
          {isAdminOrTeacher && (
            <div style={{ marginTop: 8 }}>
              <ReportBtn onClick={handleViewReport} disabled={loadingLogs}>
                {loadingLogs ? '⏳' : '📊'} Xem báo cáo kết quả học sinh
              </ReportBtn>
            </div>
          )}
        </Header>

        {/* ─── Persistent student info bar ─── */}
        {studentName && !isAdminOrTeacher && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
            background: 'linear-gradient(135deg,#f5f3ff,#ede9fe)',
            border: '1.5px solid #c4b5fd',
            borderRadius: 14, padding: '10px 16px', marginBottom: 14,
          }}>
            {/* ⭐ Stars — first, before avatar */}
            {(() => {
              const gift = classStudents.find((cs: any) => cs.id === studentId)?.gift;
              if (!gift || gift <= 0) return null;
              return (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 3,
                  background: '#fef9c3', border: '1px solid #fbbf24',
                  borderRadius: 20, padding: '4px 10px',
                  fontSize: 15, fontWeight: 800, color: '#92400e', flexShrink: 0,
                }}>
                  ⭐ {gift}
                </span>
              );
            })()}

            {/* Avatar + name */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              {studentAvatar
                ? <img src={resolveAvatarUrl(studentAvatar)} alt={studentName}
                  style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', border: '2px solid #a78bfa' }} />
                : <div style={{
                  width: 32, height: 32, borderRadius: '50%',
                  background: 'linear-gradient(135deg,#7c3aed,#a78bfa)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontWeight: 800, fontSize: 14
                }}>
                  {studentName.charAt(0).toUpperCase()}
                </div>
              }
              <span style={{ fontWeight: 700, fontSize: 14, color: '#4c1d95' }}>{studentName}</span>
            </div>

            {/* Score badges per game type */}
            {(['pronounce', 'quiz', 'match', 'sort'] as const).map(type => {
              const logs = localPlayLogs.filter(l => l.game_type === type);
              if (logs.length === 0) return null;
              const best = Math.max(...logs.map(l => l.score_pct));
              const icon = type === 'pronounce' ? '🎤' : type === 'quiz' ? '🧠' : type === 'match' ? '🎯' : '🔤';
              const label = type === 'pronounce' ? 'Phát âm' : type === 'quiz' ? 'Trắc nghiệm' : type === 'match' ? 'Ghép thẻ' : 'Sắp xếp';
              const bg = type === 'pronounce' ? '#fff7ed' : type === 'quiz' ? '#fef3c7' : type === 'match' ? '#dcfce7' : '#f5f3ff';
              const clr = type === 'pronounce' ? '#c2410c' : type === 'quiz' ? '#92400e' : type === 'match' ? '#14532d' : '#4c1d95';
              const scoreColor = best >= 80 ? '#16a34a' : best >= 50 ? '#d97706' : '#dc2626';
              return (
                <span key={type} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '4px 12px', borderRadius: 20,
                  background: bg, color: clr, fontSize: 12, fontWeight: 700,
                  border: `1px solid ${clr}33`,
                }}>
                  {icon} {label}
                  <span style={{ color: scoreColor, fontWeight: 800, marginLeft: 2 }}>{best}%</span>
                  {logs.length > 1 && <span style={{ fontSize: 10, opacity: 0.7 }}>({logs.length})</span>}
                </span>
              );
            })}

            {localPlayLogs.length === 0 && (
              <span style={{ fontSize: 12, color: '#7c3aed', opacity: 0.7 }}>Chưa có kết quả nào</span>
            )}
          </div>
        )}

        {/* Game tab bar */}
        <TabBar style={{ flexWrap: 'wrap' }}>
          <Tab active={activeTab === null} onClick={() => setActiveTab(null)}>
            📚 Ôn {vocabItems.length} từ vựng &amp; {sentenceItems.length} mẫu câu
          </Tab>
          <Tab active={activeTab === 'pronounce'} onClick={() => setActiveTab(t => t === 'pronounce' ? null : 'pronounce')} style={{ background: activeTab === 'pronounce' ? 'linear-gradient(90deg,#f97316,#ef4444)' : undefined, color: activeTab === 'pronounce' ? '#fff' : undefined }}>
            🎤 Luyện phát âm
          </Tab>
          <Tab active={activeTab === 'quiz'} onClick={() => setActiveTab(t => t === 'quiz' ? null : 'quiz')} style={{ background: activeTab === 'quiz' ? 'linear-gradient(90deg,#f59e0b,#ef4444)' : undefined, color: activeTab === 'quiz' ? '#fff' : undefined }}>
            🧠 Trắc nghiệm
          </Tab>
          <Tab active={activeTab === 'match'} onClick={() => setActiveTab(t => t === 'match' ? null : 'match')} style={{ background: activeTab === 'match' ? 'linear-gradient(90deg,#22c55e,#16a34a)' : undefined, color: activeTab === 'match' ? '#fff' : undefined }}>
            🎯 Ghép thẻ
          </Tab>
          {sentenceItems.length > 0 && (
            <Tab active={activeTab === 'sort'} onClick={() => setActiveTab(t => t === 'sort' ? null : 'sort')} style={{ background: activeTab === 'sort' ? 'linear-gradient(90deg,#a78bfa,#7c3aed)' : undefined, color: activeTab === 'sort' ? '#fff' : undefined }}>
              🔤 Sắp xếp câu
            </Tab>
          )}
        </TabBar>

        {/* ── FLASHCARD SECTION ── always visible when no game tab is open ── */}
        {activeTab === null && (
          <>
            {displayCards.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#bbb', padding: '40px', background: '#fff', borderRadius: 12 }}>
                Chưa có từ vựng nào trong bài ôn tập này.
              </div>
            ) : (
              <>
                <FlashcardScene onClick={handleFlip}>
                  <FlashcardInner flipped={flipped}>
                    <CardFace>
                      {reverseMode ? (
                        <>
                          <CardFront $fs={cardFontSize}>{currentCard?.back}</CardFront>
                          <CardHint>Bấm để lật thẻ (xem tiếng Anh)</CardHint>
                        </>
                      ) : (
                        <>
                          <CardFront $fs={cardFontSize}>{currentCard?.front}</CardFront>
                          {currentCard?.phonetic && (
                            <CardPhonetic>/{currentCard.phonetic.replace(/^\/|\/$/g, '')}/</CardPhonetic>
                          )}
                          <CardHint>Bấm để lật thẻ</CardHint>
                        </>
                      )}
                    </CardFace>
                    <CardFace back
                      onDragOver={isAdminOrTeacher && flipped ? (e) => { e.preventDefault(); setCardDragOver(true); } : undefined}
                      onDragLeave={isAdminOrTeacher && flipped ? () => setCardDragOver(false) : undefined}
                      onDrop={isAdminOrTeacher && flipped ? (e) => {
                        e.preventDefault();
                        setCardDragOver(false);
                        const file = e.dataTransfer.files?.[0];
                        if (file && currentCard) setRembgPending({ file, itemId: currentCard.id });
                      } : undefined}
                      style={cardDragOver ? { outline: '3px dashed #6366f1', outlineOffset: '-4px' } : undefined}
                    >
                      {/* ─── REMBG LOADING OVERLAY ─── */}
                      {removingBg && currentCard && (
                        <div style={{ position: 'absolute', inset: 0, zIndex: 40, borderRadius: 16, background: 'rgba(0,0,0,0.75)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14 }} onClick={e => e.stopPropagation()}>
                          <div style={{ fontSize: 40, animation: 'spin 1s linear infinite' }}>⚙️</div>
                          <div style={{ color: '#fff', fontWeight: 700, fontSize: '1rem' }}>Đang xóa nền ảnh...</div>
                          <div style={{ color: '#a5b4fc', fontSize: '0.82rem' }}>Vui lòng đợi xoá nền ảnh, có thể mất khoảng 10–20 giây</div>
                        </div>
                      )}

                      {/* ─── REMBG CHOICE POPUP ─── */}
                      {rembgPending && currentCard && rembgPending.itemId === currentCard.id && (
                        <div
                          style={{ position: 'absolute', inset: 0, zIndex: 35, borderRadius: 16, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          onClick={e => e.stopPropagation()}
                        >
                          <div style={{ background: '#fff', borderRadius: 16, padding: '24px 28px', maxWidth: 300, width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.25)', textAlign: 'center' }}>
                            <div style={{ fontSize: '2rem', marginBottom: 8 }}>🖼️</div>
                            <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 6, color: '#1e293b' }}>Xử lý ảnh</div>
                            <div style={{ fontSize: '0.82rem', color: '#64748b', marginBottom: 18 }}>
                              Bạn có muốn <strong>xóa nền</strong> ảnh trước khi crop không?
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                              <button
                                onClick={handleRemoveBgAndCrop}
                                style={{ padding: '10px 0', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                              >
                                ✨ Xóa nền + Crop
                              </button>
                              <button
                                onClick={() => { const { file, itemId } = rembgPending!; setRembgPending(null); openCropEditor(file, itemId); }}
                                style={{ padding: '9px 0', borderRadius: 10, border: '1.5px solid #c7d2fe', background: '#f0f4ff', color: '#4f46e5', fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer' }}
                              >
                                📐 Crop thôi
                              </button>
                              <button
                                onClick={() => setRembgPending(null)}
                                style={{ padding: '8px 0', borderRadius: 10, border: '1px solid #e2e8f0', background: '#fff', color: '#94a3b8', fontWeight: 500, fontSize: '0.85rem', cursor: 'pointer' }}
                              >
                                Hủy
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* ─── CROP EDITOR OVERLAY ─── */}
                      {cropState && currentCard && cropState.itemId === currentCard.id && (
                        <div
                          style={{ position: 'absolute', inset: 0, zIndex: 30, borderRadius: 16, overflow: 'hidden', background: '#111' }}
                          onClick={e => e.stopPropagation()}
                        >
                          {/* Canvas */}
                          <canvas
                            ref={cropCanvasRef}
                            width={CROP_W}
                            height={CROP_H}
                            style={{
                              width: '100%', height: 'calc(100% - 40px)', display: 'block',
                              cursor: cropDragging ? 'grabbing' : 'grab',
                            }}
                            onMouseDown={handleCropMouseDown}
                            onMouseMove={handleCropMouseMove}
                            onMouseUp={handleCropMouseUp}
                            onMouseLeave={handleCropMouseUp}
                            onWheel={handleCropWheel}
                          />
                          {/* Controls bar */}
                          <div style={{
                            position: 'absolute', bottom: 0, left: 0, right: 0, height: 40,
                            background: 'rgba(15,15,20,0.92)',
                            display: 'flex', alignItems: 'center', gap: 8, padding: '0 10px',
                          }}>
                            <span style={{ fontSize: 10, color: '#94a3b8', whiteSpace: 'nowrap' }}>🔍 Zoom</span>
                            <input
                              type="range" min={0.1} max={4} step={0.02}
                              value={cropScale}
                              onChange={e => {
                                const ns = Number(e.target.value);
                                const r = ns / cropScale;
                                setCropOffset(prev => ({
                                  x: CROP_W / 2 - (CROP_W / 2 - prev.x) * r,
                                  y: CROP_H / 2 - (CROP_H / 2 - prev.y) * r,
                                }));
                                setCropScale(ns);
                              }}
                              style={{ flex: 1, accentColor: '#6366f1' }}
                            />
                            <span style={{ fontSize: 10, color: '#64748b', minWidth: 32 }}>{Math.round(cropScale * 100)}%</span>
                            <button
                              onClick={handleCropConfirm}
                              style={{
                                padding: '4px 12px', borderRadius: 6, border: 'none',
                                background: '#22c55e', color: '#fff', fontWeight: 700,
                                fontSize: 12, cursor: 'pointer',
                              }}
                            >✅ OK</button>
                            <button
                              onClick={handleCropCancel}
                              style={{
                                padding: '4px 8px', borderRadius: 6, border: 'none',
                                background: '#ef4444', color: '#fff', fontWeight: 700,
                                fontSize: 12, cursor: 'pointer',
                              }}
                            >✕</button>
                          </div>
                          {/* Instruction */}
                          {!cropImgEl && (
                            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', fontSize: 13 }}>
                              ⏳ Đang tải ảnh...
                            </div>
                          )}
                          {cropImgEl && (
                            <div style={{
                              position: 'absolute', top: 6, left: '50%', transform: 'translateX(-50%)',
                              background: 'rgba(0,0,0,0.6)', color: '#cbd5e1',
                              fontSize: 10, padding: '2px 10px', borderRadius: 6, pointerEvents: 'none',
                            }}>
                              Kéo để di chuyển · Cuộn để zoom · nhấn ✅ OK để lưu
                            </div>
                          )}
                        </div>
                      )}

                      {/* Normal card back content (hidden during crop) */}
                      {!cropState || cropState.itemId !== currentCard?.id ? (
                        <>
                          {reverseMode ? (
                            /* Reverse mode back = English + phonetic, centered */
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '28px 36px' }}>
                              <CardBack $fs={cardFontSize}>{currentCard?.front}</CardBack>
                              {currentCard?.phonetic && (
                                <CardPhonetic>/{currentCard.phonetic.replace(/^\/|\/$/g, '')}/</CardPhonetic>
                              )}
                            </div>
                          ) : (currentCard?.custom_image_url || currentCard?.image_url) ? (
                            <>
                              <CardImage
                                src={resolveImageUrl((currentCard?.custom_image_url || currentCard?.image_url)!)}
                                alt={currentCard?.front}
                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                              />
                              {currentCard?.custom_image_url && isAdminOrTeacher && (
                                <div style={{
                                  position: 'absolute', top: 8, right: 8,
                                  background: 'rgba(99,102,241,0.85)',
                                  color: '#fff', fontSize: 10, fontWeight: 700,
                                  padding: '2px 7px', borderRadius: 8,
                                }}>📸 Ảnh tùy chỉnh</div>
                              )}
                              <CardBackOverlay>
                                <CardBack $fs={cardFontSize} style={{ textAlign: 'center', fontSize: `${Math.round(cardFontSize * 0.63)}px` }}>{currentCard?.back}</CardBack>
                              </CardBackOverlay>
                            </>
                          ) : (
                            <div style={{ padding: '28px 36px', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', flexDirection: 'column', gap: 8 }}>
                              <CardBack $fs={cardFontSize} style={{ textAlign: 'center' }}>{currentCard?.back}</CardBack>
                              {isAdminOrTeacher && flipped && (
                                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                                  📂 Kéo thả ảnh vào đây hoặc nhấn nút bên dưới để thêm ảnh
                                </div>
                              )}
                            </div>
                          )}

                          {/* Upload controls — teacher only */}
                          {isAdminOrTeacher && flipped && currentCard && (
                            <div
                              style={{
                                position: 'absolute', bottom: 8, right: 8,
                                display: 'flex', gap: 6, zIndex: 10,
                              }}
                              onClick={e => e.stopPropagation()}
                            >
                              <input
                                ref={imgInputRef}
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={e => {
                                  const f = e.target.files?.[0];
                                  if (f && currentCard) {
                                    // Show choice popup instead of opening crop editor directly
                                    setRembgPending({ file: f, itemId: currentCard.id });
                                  }
                                  e.target.value = '';
                                }}
                              />
                              <button
                                onClick={() => imgInputRef.current?.click()}
                                disabled={uploadingImageId === currentCard.id}
                                title="Upload ảnh tùy chỉnh"
                                style={{
                                  padding: '5px 10px', borderRadius: 8, border: 'none',
                                  background: 'rgba(99,102,241,0.9)', color: '#fff',
                                  fontSize: 11, fontWeight: 700, cursor: 'pointer',
                                  display: 'flex', alignItems: 'center', gap: 4,
                                }}
                              >
                                {uploadingImageId === currentCard.id ? '⏳' : '📷'}
                                {currentCard.custom_image_url ? 'Đổi ảnh' : 'Thêm ảnh'}
                              </button>
                              {currentCard.custom_image_url && (
                                <button
                                  onClick={() => handleImageDelete(currentCard.id)}
                                  disabled={uploadingImageId === currentCard.id}
                                  title="Xóa ảnh tùy chỉnh"
                                  style={{
                                    padding: '5px 8px', borderRadius: 8, border: 'none',
                                    background: 'rgba(239,68,68,0.88)', color: '#fff',
                                    fontSize: 11, fontWeight: 700, cursor: 'pointer',
                                  }}
                                >🗑️</button>
                              )}
                            </div>
                          )}
                        </>
                      ) : null}
                    </CardFace>
                  </FlashcardInner>
                </FlashcardScene>

                <NavRow>
                  <NavBtn onClick={handlePrev} disabled={cardIndex === 0}><ChevronLeft size={18} /></NavBtn>
                  <Counter>{cardIndex + 1} / {displayCards.length}</Counter>
                  <NavBtn onClick={handleNext} disabled={cardIndex === displayCards.length - 1}><ChevronRight size={18} /></NavBtn>
                </NavRow>

                <CtrlRow>
                  <CtrlBtn onClick={handleReset}><RotateCcw size={13} />Bắt đầu lại</CtrlBtn>
                  <CtrlBtn onClick={() => setShuffled(s => !s)}>
                    <Shuffle size={13} />{shuffled ? 'Bỏ xáo trộn' : 'Xáo trộn'}
                  </CtrlBtn>
                  <AudioCtrlBtn onClick={() => currentCard && playTTSAudio(reverseMode ? currentCard.back : currentCard.front)} disabled={loadingTTS}>
                    <Volume2 size={15} />{loadingTTS ? 'Đang tải...' : 'Nghe phát âm'}
                  </AudioCtrlBtn>
                  <CtrlBtn onClick={() => setShowSettings(true)} title="Cài đặt">
                    <Settings size={13} />Cài đặt
                  </CtrlBtn>
                </CtrlRow>
              </>
            )}

            {/* Vocab list under flashcard */}
            <SectionTitle>📚 Danh sách từ vựng</SectionTitle>
            <SentenceList>{vocabItems.map(renderItemRow)}</SentenceList>

            {/* Sentence section */}
            {sentenceItems.length > 0 && (
              <>
                <SectionTitle>💬 Danh sách mẫu câu</SectionTitle>
                <SentenceList>{sentenceItems.map(renderItemRow)}</SentenceList>
              </>
            )}

            {/* Admin add button */}
            {isAdminOrTeacher && !showAddForm && (
              <AddBtn onClick={() => setShowAddForm(true)} style={{ marginTop: 16 }}>
                <Plus size={13} />Thêm từ/câu mới
              </AddBtn>
            )}
          </>
        )}


        {/* ─── QUIZ TAB ─── */}
        {activeTab === 'quiz' && (
          <>
            <ReviewQuizGame items={vocabItems} onGameComplete={(pct) => handleGameComplete('quiz', pct)} />
          </>
        )}

        {/* ─── MATCH TAB ─── */}
        {activeTab === 'match' && (
          <>
            <div style={{ background: 'linear-gradient(135deg,#f0fdf4,#fff)', borderRadius: 14, padding: '16px 20px', marginBottom: 18, border: '2px solid #bbf7d0' }}>
              <div style={{ fontWeight: 700, color: '#14532d', marginBottom: 4 }}>🎯 Ghép thẻ</div>
              <div style={{ fontSize: 13, color: '#4b5563' }}>Bấm một thẻ tiếng Anh, rồi bấm thẻ tiếng Việt tương ứng để ghép cặp. Ghép hết là thắng!</div>
            </div>
            <ReviewMatchGame items={vocabItems} onGameComplete={(pct) => handleGameComplete('match', pct)} />
          </>
        )}

        {/* ─── SORT SENTENCE TAB ─── */}
        {sentenceItems.length > 0 && activeTab === 'sort' && (
          <>
            <div style={{ background: 'linear-gradient(135deg,#f5f3ff,#fff)', borderRadius: 14, padding: '16px 20px', marginBottom: 18, border: '2px solid #ddd6fe' }}>
              <div style={{ fontWeight: 700, color: '#4c1d95', marginBottom: 4 }}>🔤 Sắp xếp câu</div>
              <div style={{ fontSize: 13, color: '#4b5563' }}>Đọc nghĩa tiếng Việt, bấm vào các từ để sắp xếp thành câu tiếng Anh đúng!</div>
            </div>
            <ReviewSortGame sentenceItems={sentenceItems} onGameComplete={(pct) => handleGameComplete('sort', pct)} />
          </>
        )}

        {/* ─── PRONOUNCE TAB ─── */}
        {activeTab === 'pronounce' && (
          <>
            <div style={{ background: 'linear-gradient(135deg,#fff7ed,#fff)', borderRadius: 14, padding: '16px 20px', marginBottom: 18, border: '2px solid #fed7aa' }}>
              <div style={{ fontWeight: 700, color: '#c2410c', marginBottom: 4 }}>🎤 Luyện phát âm</div>
              <div style={{ fontSize: 13, color: '#4b5563' }}>Bấm nút micro rồi nói to từ/câu hiển thị trên màn hình. Đạt 70% điểm là qua!</div>
            </div>
            <ReviewPronounceGame
              items={items.filter(i => !i.is_deleted)}
              onGameComplete={(pct) => handleGameComplete('pronounce', pct)}
              initialPassedIds={pronunciationPassedIds}
              onItemPassed={handlePronounceItemPassed}
            />
          </>
        )}

        {/* ── ADD ITEM FORM ── */}
        {isAdminOrTeacher && showAddForm && (
          <AddItemForm>
            <AddSelect value={newType} onChange={e => setNewType(e.target.value as any)}>
              <option value="vocab">📚 Từ vựng</option>
              <option value="sentence">💬 Mẫu câu</option>
            </AddSelect>
            <AddInput
              placeholder="Tiếng Anh (front)"
              value={newFront}
              onChange={e => setNewFront(e.target.value)}
            />
            <AddInput
              placeholder="Phiên âm (phonics)"
              value={newPhonetic}
              onChange={e => setNewPhonetic(e.target.value)}
              style={{ maxWidth: 160 }}
            />
            <AddInput
              placeholder="Tiếng Việt (back)"
              value={newBack}
              onChange={e => setNewBack(e.target.value)}
            />
            <SaveBtn onClick={handleAddItem} disabled={saving}>{saving ? '⏳' : 'Thêm'}</SaveBtn>
            <CtrlBtn onClick={() => setShowAddForm(false)}><X size={13} />Huỷ</CtrlBtn>
          </AddItemForm>
        )}
      </Container>

      {/* ─── Settings Modal ─── */}
      {showSettings && (
        <SettingsOverlay onClick={(e) => { if (e.target === e.currentTarget) setShowSettings(false); }}>
          <SettingsCard>
            <SettingsTitle>
              <Settings size={18} /> Cài đặt Flashcard
            </SettingsTitle>

            <SettingRow>
              <SettingLabel>
                🔄 Chế độ ôn ngược (Việt → Anh)
                <small>Mặt trước thẻ hiển thị tiếng Việt, lật ra tiếng Anh</small>
              </SettingLabel>
              <Toggle on={reverseMode} onClick={() => { setReverseMode(v => !v); setFlipped(false); }} />
            </SettingRow>

            <SettingRow>
              <SettingLabel>
                🔊 Tự động nghe phát âm
                <small>Tự động đọc phát âm khi sang thẻ mới</small>
              </SettingLabel>
              <Toggle on={autoPlayTTS} onClick={() => setAutoPlayTTS(v => !v)} />
            </SettingRow>

            <SettingRow>
              <SettingLabel>
                💬 Ôn cả mẫu câu
                <small>Gộp mẫu câu vào bộ flashcard để lật và ôn tập</small>
              </SettingLabel>
              <Toggle on={includeSentences} onClick={() => { setIncludeSentences(v => !v); setFlipped(false); }} />
            </SettingRow>

            {/* Font size picker */}
            <SettingRow style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 10 }}>
              <SettingLabel>
                🔤 Cỡ chữ trên thẻ
                <small>Chọn cỡ chữ vừa ý cho màn hình đang chiếu</small>
              </SettingLabel>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {FONT_SIZES.map((opt, i) => (
                  <button
                    key={opt.value}
                    onClick={() => setCardFontSize(opt.value)}
                    title={opt.title}
                    style={{
                      width: 44 + i * 8, height: 44 + i * 8,
                      borderRadius: 10,
                      border: cardFontSize === opt.value
                        ? '2.5px solid #667eea'
                        : '2px solid #e2e8f0',
                      background: cardFontSize === opt.value
                        ? 'linear-gradient(135deg,#667eea,#764ba2)'
                        : '#f8fafc',
                      color: cardFontSize === opt.value ? '#fff' : '#334155',
                      fontWeight: 800,
                      fontSize: 12 + i * 4,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: cardFontSize === opt.value
                        ? '0 4px 12px rgba(102,126,234,0.35)'
                        : '0 1px 3px rgba(0,0,0,0.06)',
                    }}
                  >
                    a
                  </button>
                ))}
              </div>
            </SettingRow>

            <SettingsCloseBtn onClick={() => setShowSettings(false)}>Xong</SettingsCloseBtn>
          </SettingsCard>
        </SettingsOverlay>
      )}

      {/* ─── Student Name Modal ─── */}
      {showStudentModal && !isAdminOrTeacher && !isStudent && (
        <StudentOverlay>
          <StudentCard>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}>
                <img
                  src="/kt-english-logo.png"
                  alt="KT English Logo"
                  style={{ width: 100, height: 'auto', filter: 'drop-shadow(0 4px 12px rgba(37,99,235,0.25))', objectFit: 'contain' }}
                />
              </div>
              <div style={{ fontSize: 20, fontWeight: 900, color: '#1e1b4b', letterSpacing: -0.3 }}>
                Nhập tên học sinh để ôn tập
              </div>
            </div>

            {/* Search input */}
            <div style={{ position: 'relative', marginBottom: 10 }}>
              <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 18, pointerEvents: 'none' }}>🔍</span>
              <StudentSearchInput
                autoFocus
                value={studentSearch}
                onChange={e => { setStudentSearch(e.target.value); setSelectedStudentItem(null); }}
                placeholder="Tìm tên học sinh..."
                onKeyDown={e => { if (e.key === 'Enter' && selectedStudentItem) handleConfirmStudent(); }}
              />
            </div>
            {/* Hint: must select from list */}
            <div style={{ fontSize: 12, color: '#fd0a0aff', marginBottom: 6, textAlign: 'center' }}>
              Vui lòng tìm tên, và chọn tên từ danh sách bên dưới để bắt đầu ↓
            </div>

            {/* Student list — only show when user has typed something */}
            {classStudents.length > 0 && studentSearch.trim().length > 0 && (
              <StudentList>
                {classStudents
                  .filter(s => !studentSearch || s.name.toLowerCase().includes(studentSearch.toLowerCase()))
                  .slice(0, 20)
                  .map((s: any) => (
                    <StudentItem
                      key={s.id}
                      $selected={selectedStudentItem?.id === s.id}
                      onClick={() => {
                        setSelectedStudentItem({ id: s.id, name: s.name });
                        setStudentSearch(s.name);
                      }}
                    >
                      <StudentAvatar>
                        {s.avatar
                          ? <img src={resolveAvatarUrl(s.avatar)} alt={s.name} />
                          : s.name.charAt(0).toUpperCase()}
                      </StudentAvatar>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name}</div>
                        <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 1 }}>ID: #{s.id}</div>
                      </div>
                      {selectedStudentItem?.id === s.id && (
                        <span style={{ marginLeft: 8, fontSize: 18, flexShrink: 0 }}>✅</span>
                      )}
                    </StudentItem>
                  ))}
              </StudentList>
            )}

            <StartStudyBtn
              $ready={!!selectedStudentItem}
              onClick={handleConfirmStudent}
              disabled={!selectedStudentItem}
            >
              🚀 Bắt đầu ôn tập!
            </StartStudyBtn>
          </StudentCard>
        </StudentOverlay>
      )}

      {/* ─── Report Modal ─── */}
      {showReportModal && (
        <SettingsOverlay onClick={e => { if (e.target === e.currentTarget) setShowReportModal(false); }}>
          <SettingsCard style={{ maxWidth: 680, maxHeight: '85vh', overflowY: 'auto' }}>
            <SettingsTitle>
              <span style={{ fontSize: 20 }}>📊</span> Báo cáo kết quả ôn tập
            </SettingsTitle>
            {playLogs.length === 0 && classStudents.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: 15 }}>
                Chưa có học sinh nào ôn tập bài này.
              </div>
            ) : (
              (() => {
                // Học sinh chưa làm bài: có trong classStudents nhưng không có trong groupedReport
                const playedIds = new Set(
                  groupedReport
                    .filter(g => g.student_id != null)
                    .map(g => g.student_id)
                );
                const playedNames = new Set(groupedReport.map(g => g.student_name.toLowerCase().trim()));
                const notPlayed = classStudents.filter(cs =>
                  !playedIds.has(cs.id) && !playedNames.has((cs.name || '').toLowerCase().trim())
                );

                return (
                  <div style={{ overflowX: 'auto', marginTop: 12 }}>
                    {/* Summary badges */}
                    <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
                      <span style={{ padding: '4px 12px', borderRadius: 20, background: '#dcfce7', color: '#15803d', fontWeight: 700, fontSize: 13 }}>
                        ✅ Đã làm: {groupedReport.length} học sinh
                      </span>
                      {notPlayed.length > 0 && (
                        <span style={{ padding: '4px 12px', borderRadius: 20, background: '#fef3c7', color: '#92400e', fontWeight: 700, fontSize: 13 }}>
                          ⏳ Chưa làm: {notPlayed.length} học sinh
                        </span>
                      )}
                    </div>

                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: 'linear-gradient(135deg,#667eea,#764ba2)', color: '#fff' }}>
                          <th style={{ padding: '10px 14px', textAlign: 'left', borderRadius: '8px 0 0 0', fontWeight: 700, minWidth: 160 }}>Học sinh</th>
                          <th style={{ padding: '10px 14px', textAlign: 'center', fontWeight: 700 }}>🎤 Phát âm</th>
                          <th style={{ padding: '10px 14px', textAlign: 'center', fontWeight: 700 }}>🧠 Trắc nghiệm</th>
                          <th style={{ padding: '10px 14px', textAlign: 'center', fontWeight: 700 }}>🎯 Ghép thẻ</th>
                          <th style={{ padding: '10px 14px', textAlign: 'center', borderRadius: '0 8px 0 0', fontWeight: 700 }}>🔤 Sắp xếp</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Học sinh đã làm */}
                        {groupedReport.map((student, i) => {
                          const av = getStudentAvatarById(student.student_id);
                          return (
                            <tr key={student.student_name} style={{ background: i % 2 === 0 ? '#f8fafc' : '#fff', borderBottom: '1px solid #f1f5f9' }}>
                              <td style={{ padding: '10px 14px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                  {av
                                    ? <img src={resolveAvatarUrl(av)} alt={student.student_name} style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                                    : <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg,#667eea,#764ba2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 14, flexShrink: 0 }}>
                                      {student.student_name.charAt(0).toUpperCase()}
                                    </div>
                                  }
                                  <span style={{ fontWeight: 700, color: '#1e293b' }}>{student.student_name}</span>
                                </div>
                              </td>
                              {(['pronounce', 'quiz', 'match', 'sort'] as const).map(type => {
                                const logs = student.logs[type];
                                if (logs.length === 0) return (
                                  <td key={type} style={{ padding: '10px 14px', textAlign: 'center', color: '#cbd5e1', fontSize: 20 }}>—</td>
                                );
                                const best = Math.max(...logs.map(l => l.score_pct));
                                const latest = logs[logs.length - 1];
                                const scoreColor = best >= 80 ? '#16a34a' : best >= 50 ? '#d97706' : '#dc2626';
                                return (
                                  <td key={type} style={{ padding: '10px 14px', textAlign: 'center' }}>
                                    <div style={{ fontWeight: 800, fontSize: 18, color: scoreColor }}>{best}%</div>
                                    {logs.length > 1 && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>{logs.length} lần</div>}
                                    <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
                                      {new Date(latest.played_at).toLocaleString('vi-VN', { hour12: false, day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                    </div>
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}

                        {/* Divider nếu có 2 nhóm */}
                        {notPlayed.length > 0 && groupedReport.length > 0 && (
                          <tr>
                            <td colSpan={5} style={{ padding: '8px 14px', background: '#fef9c3', borderTop: '2px dashed #fde68a', borderBottom: '2px dashed #fde68a' }}>
                              <span style={{ fontSize: 12, fontWeight: 700, color: '#92400e' }}>⏳ Học sinh chưa làm bài ({notPlayed.length})</span>
                            </td>
                          </tr>
                        )}

                        {/* Học sinh chưa làm */}
                        {notPlayed.map((cs: any, i: number) => (
                          <tr key={`not-${cs.id}`} style={{ background: i % 2 === 0 ? '#fffbeb' : '#fefce8', borderBottom: '1px solid #fef3c7', opacity: 0.85 }}>
                            <td style={{ padding: '10px 14px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                {cs.avatar
                                  ? <img src={resolveAvatarUrl(cs.avatar)} alt={cs.name} style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                                  : <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontWeight: 800, fontSize: 14, flexShrink: 0 }}>
                                    {(cs.name || '?').charAt(0).toUpperCase()}
                                  </div>
                                }
                                <div>
                                  <span style={{ fontWeight: 700, color: '#475569' }}>{cs.name}</span>
                                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Chưa làm bài</div>
                                </div>
                              </div>
                            </td>
                            {(['pronounce', 'quiz', 'match', 'sort'] as const).map(type => (
                              <td key={type} style={{ padding: '10px 14px', textAlign: 'center', color: '#d1d5db', fontSize: 18 }}>—</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()
            )}
            <SettingsCloseBtn onClick={() => setShowReportModal(false)} style={{ marginTop: 20 }}>Đóng</SettingsCloseBtn>
          </SettingsCard>
        </SettingsOverlay>
      )}
    </PageWrapper>
  );
};

export default ReviewDetailPage;
